import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

import { BaseServices } from 'src/app/kuba/kuba.services';
import { TranslateService } from '@ngx-translate/core';
import { AppraisalMeetingService } from '../../services/appraisal-meeting.service';
import { AppraisalTemplateService } from '../../../templates/services/appraisal-template.service';
import { AppraisalSettingsService } from '../../../settings/services/appraisal-settings.service';
import { AppraisalRelationshipStatus } from '../../../settings/models/appraisal-relationship-status.enum';
import { AppraisalMeetingCreateDto } from '../../models';

/**
 * Dropdown-friendly projection of {@link AppraisalMeetingEmployeeOptionDto}, with
 * the fields needed to render the status badge and drive the confirmation flow.
 */
interface EmployeeOption {
  label: string;
  value: number;
  relationshipStatus: AppraisalRelationshipStatus | null;
  statusKind: 'active' | 'pending' | 'none' | 'rejected' | 'revoked';
}

@Component({
  selector: 'appraisal-meeting-create',
  templateUrl: './appraisal-meeting-create.component.html',
  styleUrls: ['./appraisal-meeting-create.component.scss'],
  standalone: false
})
export class AppraisalMeetingCreateComponent implements OnInit, OnDestroy {
  private readonly notifications = inject(NotificationService);

  form: FormGroup;
  isSubmitting = false;

  templates: { label: string; value: number }[] = [];
  employees: EmployeeOption[] = [];
  employeePlaceholder = 'Select Employee';
  isLoadingEmployees = false;
  minStartDate = '';

  // Confirmation dialog state for unmanaged / pending employees.
  confirmUnmanagedVisible = false;
  confirmUnmanagedMessageKey = '';
  private pendingSubmitDto: AppraisalMeetingCreateDto | null = null;
  private templateSelectionSubscription?: Subscription;
  private startTimeSubscription?: Subscription;
  // Tracks the StartTime value before the most recent change, so we can
  // preserve the user-selected duration when auto-shifting EndTime.
  private previousStartTime = '09:00';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private meetingService: AppraisalMeetingService,
    private templateService: AppraisalTemplateService,
    private settingsService: AppraisalSettingsService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.minStartDate = this.toDateInputToday();
    this.employeePlaceholder = this.resolveTranslateLabel(
      'SELECT_EMPLOYEE_PLACEHOLDER',
      'Select Employee'
    );
    this.loadTemplates();
    this.setupTemplateEmployeeFiltering();
    this.setupStartTimeAutoShift();
  }

  ngOnDestroy(): void {
    this.templateSelectionSubscription?.unsubscribe();
    this.startTimeSubscription?.unsubscribe();
  }

  // Teams-style behavior: when the manager moves StartTime forward (or past EndTime),
  // shift EndTime by the same delta so the meeting duration is preserved and no
  // validation error appears. Mirrors Outlook/Teams scheduling UX.
  private setupStartTimeAutoShift(): void {
    const startCtrl = this.form.get('StartTime');
    const endCtrl = this.form.get('EndTime');
    if (!startCtrl || !endCtrl) return;

    this.startTimeSubscription = startCtrl.valueChanges.subscribe((newStart: string | null) => {
      if (!newStart) return;
      const prevStartMin = this.timeStringToMinutes(this.previousStartTime);
      const newStartMin = this.timeStringToMinutes(newStart);
      const endMin = this.timeStringToMinutes(endCtrl.value || '00:00');

      // Preserve the previously-set duration (fallback 30 min if it had collapsed).
      const duration = Math.max(endMin - prevStartMin, 30);
      let newEndMin = newStartMin + duration;
      // Cap at 23:45 to stay within the same day; user can extend explicitly if needed.
      if (newEndMin > 23 * 60 + 45) newEndMin = 23 * 60 + 45;

      this.previousStartTime = newStart;
      endCtrl.setValue(this.minutesToTimeString(newEndMin), { emitEvent: false });
    });
  }

  private minutesToTimeString(total: number): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${pad(h)}:${pad(m)}`;
  }

  private buildForm(): void {
    this.form = this.fb.group({
      TemplateId: [null, Validators.required],
      EmployeeUserId: [null, Validators.required],
      Title: ['', [Validators.required, Validators.maxLength(255)]],
      // Teams-style split: one Start date + Start time + End time (same-day).
      StartDate: [null, Validators.required],
      StartTime: ['09:00', Validators.required],
      EndTime: ['09:30', Validators.required],
      ManagerPrivateNotes: [null]
    }, { validators: [this.endTimeAfterStartValidator, this.startTimeNotInPastValidator] });
  }

  // EndTime (HH:MM) must be strictly after StartTime on the same day.
  // Both are simple "HH:MM" strings so lexicographic compare is correct.
  private endTimeAfterStartValidator = (group: FormGroup) => {
    const start = group.get('StartTime')?.value;
    const end = group.get('EndTime')?.value;
    if (!start || !end) return null;
    return end > start ? null : { endTimeBeforeStart: true };
  };

  /**
   * Group-level validator that rejects a `StartTime` in the past when `StartDate` is today.
   *
   * The check is intentionally skipped for future dates — a meeting scheduled tomorrow
   * or later is always valid regardless of the selected time.
   *
   * Returns `{ startTimeInPast: true }` when the condition is violated, otherwise `null`.
   */
  private startTimeNotInPastValidator = (group: FormGroup) => {
    const date = group.get('StartDate')?.value as string | null;
    const time = group.get('StartTime')?.value as string | null;
    if (!date || !time) return null;
    if (date !== this.toDateInputToday()) return null;
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    return this.timeStringToMinutes(time) >= currentMinutes ? null : { startTimeInPast: true };
  };

  get durationLabel(): string | null {
    const start = this.form?.get('StartTime')?.value as string | null;
    const end = this.form?.get('EndTime')?.value as string | null;
    if (!start || !end) return null;
    const minutes = this.timeStringToMinutes(end) - this.timeStringToMinutes(start);
    if (minutes <= 0) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  }

  private timeStringToMinutes(value: string): number {
    const [h, m] = value.split(':').map(n => parseInt(n, 10));
    return (isNaN(h) ? 0 : h) * 60 + (isNaN(m) ? 0 : m);
  }

  // Builds an ISO string with the browser's UTC offset appended
  // (e.g. "2026-05-22T13:00:00+02:00"). The offset is REQUIRED — without it
  // the backend deserializes to a DateTime with Kind=Unspecified, and
  // IcsCalendarBuilder.FormatIcsUtc then treats the value as the *server's*
  // local time when calling ToUniversalTime(). On a UTC server that means
  // 13:00 ends up emitted as 13:00 UTC in the .ics, and a Norwegian
  // recipient's calendar (CEST = UTC+2) renders the event at 15:00 — two
  // hours ahead of what the manager actually scheduled. Including the
  // offset gives the backend an unambiguous instant in time regardless of
  // the server's timezone, and getTimezoneOffset() honors DST so winter
  // (CET, +01:00) and summer (CEST, +02:00) both work correctly.
  private composeLocalIso(date: string, time: string): string {
    const localDate = new Date(`${date}T${time}:00`);
    const offsetMinutes = -localDate.getTimezoneOffset(); // +120 for CEST, -300 for EST
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMinutes);
    const pad = (n: number) => String(n).padStart(2, '0');
    const offset = `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
    return `${date}T${time}:00${offset}`;
  }

  private setupTemplateEmployeeFiltering(): void {
    const templateControl = this.form.get('TemplateId');
    const employeeControl = this.form.get('EmployeeUserId');

    this.templateSelectionSubscription = templateControl?.valueChanges.subscribe(
      templateId => {
        employeeControl?.setValue(null);
        this.loadEmployees(templateId);
      }
    );
  }

  private loadEmployees(templateId?: number | null): void {
    if (!templateId) {
      this.employees = [];
      this.isLoadingEmployees = false;
      return;
    }

    this.isLoadingEmployees = true;
    this.settingsService
      .getBusinessEmployeesForMeetingCreation(+BaseServices.UserId, Number(templateId))
      .subscribe({
        next: employees => {
          this.employees = employees
            .map<EmployeeOption>(e => ({
              label: this.normalizeDisplayLabel(e.Name, `Employee #${e.EmployeeUserId}`),
              value: e.EmployeeUserId,
              relationshipStatus: e.RelationshipStatus,
              statusKind: this.toStatusKind(e.RelationshipStatus)
            }))
            .filter(
              (employee, index, array) =>
                employee.label.length > 0 &&
                array.findIndex(e => e.value === employee.value) === index
            )
            .sort((a, b) => this.compareEmployeeOptions(a, b));
          this.isLoadingEmployees = false;
        },
        error: () => {
          this.employees = [];
          this.isLoadingEmployees = false;
        }
      });
  }

  private loadTemplates(): void {
    this.templateService
      .getPagedList(BaseServices.BusinessId, {
        pageNumber: 1,
        pageSize: 100,
        isActive: true
      })
      .subscribe({
        next: response => {
          this.templates = (response.Items || []).map(t => ({
            label: this.normalizeDisplayLabel(t.Name, `Form #${t.Id}`),
            value: t.Id
          }));
        },
        error: () => {}
      });
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.isSubmitting) return;

    const formValue = this.form.getRawValue();
    const scheduledDateIso = this.composeLocalIso(formValue.StartDate, formValue.StartTime);
    const endTimeIso = this.composeLocalIso(formValue.StartDate, formValue.EndTime);

    const dto: AppraisalMeetingCreateDto = {
      TemplateId: formValue.TemplateId,
      EmployeeUserId: formValue.EmployeeUserId,
      Title: formValue.Title.trim(),
      ScheduledDate: scheduledDateIso,
      EndTime: endTimeIso,
      ManagerPrivateNotes: formValue.ManagerPrivateNotes?.trim() || undefined,
      BusinessId: BaseServices.BusinessId
    };

    const selectedEmployee = this.employees.find(e => e.value === formValue.EmployeeUserId);

    // When the picked employee is not yet managed (or still pending), warn the manager
    // that the meeting will be created in Draft state and cannot start until the
    // employee accepts the (auto-created) access request.
    if (selectedEmployee && selectedEmployee.statusKind !== 'active') {
      this.pendingSubmitDto = dto;
      this.confirmUnmanagedMessageKey =
        selectedEmployee.statusKind === 'pending'
          ? 'CONFIRM_CREATE_MEETING_PENDING_MESSAGE'
          : 'CONFIRM_CREATE_MEETING_UNMANAGED_MESSAGE';
      this.confirmUnmanagedVisible = true;
      return;
    }

    this.submitCreate(dto);
  }

  confirmUnmanagedAccept(): void {
    this.confirmUnmanagedVisible = false;
    if (this.pendingSubmitDto) {
      const dto = this.pendingSubmitDto;
      this.pendingSubmitDto = null;
      this.submitCreate(dto);
    }
  }

  confirmUnmanagedCancel(): void {
    this.confirmUnmanagedVisible = false;
    this.pendingSubmitDto = null;
  }

  private submitCreate(dto: AppraisalMeetingCreateDto): void {
    this.isSubmitting = true;
    this.meetingService.create(dto).subscribe({
      next: () => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.navigateToAppraisalList();
      },
      error: () => {
        this.isSubmitting = false;
        
      }
    });
  }

  onCancel(): void {
    this.navigateToAppraisalList();
  }

  goBackToList(): void {
    this.navigateToAppraisalList();
  }

  private navigateToAppraisalList(): void {
    this.router.navigate(['../../'], {
      relativeTo: this.route,
      queryParams: { tab: 'list' }
    });
  }

  getEmployeeStatusLabelKey(employee: EmployeeOption | null | undefined): string {
    switch (employee?.statusKind) {
      case 'active':
        return 'APPRAISAL_EMPLOYEE_STATUS_MANAGED';
      case 'pending':
        return 'APPRAISAL_EMPLOYEE_STATUS_PENDING';
      case 'rejected':
        return 'APPRAISAL_EMPLOYEE_STATUS_REJECTED';
      case 'revoked':
        return 'APPRAISAL_EMPLOYEE_STATUS_REVOKED';
      default:
        return 'APPRAISAL_EMPLOYEE_STATUS_NOT_MANAGED';
    }
  }

  getEmployeeStatusHelpKey(employee: EmployeeOption | null | undefined): string {
    switch (employee?.statusKind) {
      case 'active':
        return 'APPRAISAL_EMPLOYEE_STATUS_MANAGED_HELP';
      case 'pending':
        return 'APPRAISAL_EMPLOYEE_STATUS_PENDING_HELP';
      case 'rejected':
        return 'APPRAISAL_EMPLOYEE_STATUS_REJECTED_HELP';
      case 'revoked':
        return 'APPRAISAL_EMPLOYEE_STATUS_REVOKED_HELP';
      default:
        return 'APPRAISAL_EMPLOYEE_STATUS_NOT_MANAGED_HELP';
    }
  }

  getEmployeeStatusClass(employee: EmployeeOption | null | undefined): string {
    switch (employee?.statusKind) {
      case 'active':
        return 'status-active';
      case 'pending':
        return 'status-pending';
      case 'rejected':
        return 'status-rejected';
      case 'revoked':
        return 'status-revoked';
      default:
        return 'status-none';
    }
  }

  getEmployeeStatusIcon(employee: EmployeeOption | null | undefined): string {
    switch (employee?.statusKind) {
      case 'active':
        return 'fa-check-circle';
      case 'pending':
        return 'fa-clock-o';
      case 'rejected':
        return 'fa-times-circle';
      case 'revoked':
        return 'fa-ban';
      default:
        return 'fa-user-plus';
    }
  }

  private toStatusKind(
    status: AppraisalRelationshipStatus | null
  ): EmployeeOption['statusKind'] {
    if (status === null || status === undefined) {
      return 'none';
    }
    switch (status) {
      case AppraisalRelationshipStatus.Active:
        return 'active';
      case AppraisalRelationshipStatus.Pending:
        return 'pending';
      case AppraisalRelationshipStatus.Rejected:
        return 'rejected';
      case AppraisalRelationshipStatus.Revoked:
        return 'revoked';
      default:
        return 'none';
    }
  }

  private compareEmployeeOptions(a: EmployeeOption, b: EmployeeOption): number {
    const statusComparison =
      this.getEmployeeStatusSortOrder(a) - this.getEmployeeStatusSortOrder(b);

    if (statusComparison !== 0) {
      return statusComparison;
    }

    return a.label.localeCompare(b.label, undefined, {
      numeric: true,
      sensitivity: 'base'
    });
  }

  private getEmployeeStatusSortOrder(employee: EmployeeOption): number {
    switch (employee.statusKind) {
      case 'active':
        return 0;
      case 'pending':
        return 1;
      case 'none':
        return 2;
      case 'rejected':
        return 3;
      case 'revoked':
        return 4;
      default:
        return 5;
    }
  }

  private normalizeDisplayLabel(rawValue: any, fallback: string): string {
    if (typeof rawValue === 'string' && rawValue.trim().length > 0) {
      const normalized = rawValue.trim();
      if (normalized !== '[object Object]') {
        return normalized;
      }
    }

    if (rawValue && typeof rawValue === 'object') {
      const nestedValue = rawValue.Name ?? rawValue.name ?? rawValue.Label ?? rawValue.label;
      if (typeof nestedValue === 'string' && nestedValue.trim().length > 0) {
        return nestedValue.trim();
      }
    }

    return fallback;
  }

  private toDateInputToday(): string {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }

  private resolveTranslateLabel(key: string, fallback: string): string {
    const translated = this.translate.instant(key);

    if (typeof translated === 'string' && translated.trim().length > 0) {
      const normalized = translated.trim();
      if (normalized !== '[object Object]') {
        return normalized;
      }
    }

    return fallback;
  }
}
